import React from 'react'
import { useNavigate } from 'react-router-dom'
import FlatButton from '../../components/buttons/flat_button'
import XIcon from '../../components/common/x_icon'
import SparkleIcon from '../../images/landing/plans/sparkle.png'
import BoltIcon from '../../images/landing/plans/bolt.png'
import GroupIcon from '../../images/landing/plans/group.png'
import DomainIcon from '../../images/landing/plans/domain.png'
import CheckIconImage from '../../images/check_icon.png'
import DashboardLink from '../../components/layouts/side_menu/dashboard_link'
import { useAuth } from '../../hooks/auth/use_auth'

type PlanFeature = {
  name: string
  included: boolean
}

type Plan = {
  name: string
  price: string
  description: string
  features: PlanFeature[]
  buttonText: string
  recommended?: boolean
  icon: string
  notes: string[]
}

const PlanSection: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const plans: Plan[] = [
    {
      name: 'フリープラン',
      price: '0円',
      description: 'お試し利用プラン',
      features: [
        { name: '自社の企業情報の登録', included: true },
        { name: '企業検索', included: true },
        { name: '企業詳細の閲覧', included: true },
        { name: '企業足跡機能閲覧', included: true },
        { name: 'マッチングリクエスト（※1）', included: true },
        { name: '受信メッセージ閲覧', included: true },
        { name: 'AI事業アイディア提案（※2）', included: true },
        { name: '共創パートナー募集（※3）', included: true },
        { name: 'お気に入り企業機能', included: true },
      ],
      buttonText: '無料で始める',
      icon: SparkleIcon,
      notes: [
        '※1：送信5回まで、承認・受信は無制限',
        '※2：5回まで無料',
        '※3：一部閲覧可能',
      ],
    },
    {
      name: '1ヶ月プラン',
      price: '8万円',
      description: '',
      features: [
        { name: '自社の企業情報の登録', included: true },
        { name: '企業検索', included: true },
        { name: '企業詳細の閲覧', included: true },
        { name: '企業足跡機能閲覧', included: true },
        { name: 'マッチングリクエスト', included: true },
        { name: '受信メッセージ閲覧', included: true },
        { name: 'AI事業アイディア提案（※1）', included: true },
        { name: '共創パートナー募集', included: true },
        { name: 'お気に入り企業機能', included: true },
      ],
      buttonText: '会員登録に進む',
      recommended: true,
      icon: BoltIcon,
      notes: ['※1：回数制限がかかる場合あり'],
    },
    {
      name: '半年プラン',
      price: '7.5万円',
      description: '充実の使い放題プラン',
      features: [
        { name: '自社の企業情報の登録', included: true },
        { name: '企業検索', included: true },
        { name: '企業詳細の閲覧', included: true },
        { name: '企業足跡機能閲覧', included: true },
        { name: 'マッチングリクエスト', included: true },
        { name: '受信メッセージ閲覧', included: true },
        { name: 'AI事業アイディア提案（※1）', included: true },
        { name: '共創パートナー募集', included: true },
        { name: 'お気に入り企業機能', included: true },
      ],
      buttonText: '会員登録に進む',
      icon: GroupIcon,
      notes: ['※1：回数制限がかかる場合あり'],
    },
    {
      name: '年間プラン',
      price: '7万円',
      description: '充実の使い放題プラン',
      features: [
        { name: '自社の企業情報の登録', included: true },
        { name: '企業検索', included: true },
        { name: '企業詳細の閲覧', included: true },
        { name: '企業足跡機能閲覧', included: true },
        { name: 'マッチングリクエスト', included: true },
        { name: '受信メッセージ閲覧', included: true },
        { name: 'AI事業アイディア提案（※1）', included: true },
        { name: '共創パートナー募集', included: true },
        { name: 'お気に入り企業機能', included: true },
      ],
      buttonText: '会員登録に進む',
      icon: DomainIcon,
      notes: ['※1：回数制限がかかる場合あり'],
    },
  ]

  return (
    <section id="plan" className="py-20">
      <div className="w-11/12 max-w-7xl mx-auto">
        {/* 料金プラン */}
        <div className="text-left mb-16">
          <p className="text-sm mb-4">Price</p>
          <h1 className="text-3xl font-bold mb-4">料金プラン</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`border rounded-lg bg-white h-full ${plan.recommended ? 'border-main-green border-2' : 'border-main-darkgray'}`}
            >
              <div className="p-6 h-full flex flex-col">
                {/* アイコン */}
                <div className="flex justify-center mb-4 mt-4">
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center ${plan.recommended ? 'bg-main-lightgreen' : 'bg-main-gray'}`}
                  >
                    <img
                      src={plan.icon}
                      alt={plan.name}
                      className="w-7 h-7 object-contain"
                    />
                  </div>
                </div>

                {/* プラン名 */}
                <h2 className="text-xl font-bold text-center mb-2">
                  {plan.name}
                </h2>

                {/* オススメラベル */}
                {plan.recommended && (
                  <div className="flex justify-center mb-2">
                    <div className="bg-main-green text-white px-4 py-1 text-sm font-bold rounded">
                      オススメ使い放題プラン
                    </div>
                  </div>
                )}

                {/* 説明 */}
                <p className="text-sm text-center text-main-darkgray mb-4">
                  {plan.description}
                </p>

                {/* 価格 */}
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold">
                    {plan.price}
                    <span className="text-base font-normal text-main-darkgray">
                      / 月
                    </span>
                  </div>
                </div>

                {/* 機能リスト */}
                <div className="space-y-2 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      {feature.included ? (
                        <>
                          <img
                            src={CheckIconImage}
                            alt="check"
                            className="w-6 h-6 flex-shrink-0"
                          />
                          <span className="ml-2 text-base">{feature.name}</span>
                        </>
                      ) : (
                        <>
                          <XIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                          <span className="ml-2 text-base text-gray-400">
                            {feature.name}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* 注釈 */}
                <div className="mb-6 bg-main-lightgreen p-3 rounded">
                  {plan.notes.map((note, noteIndex) => (
                    <p key={noteIndex} className="text-xs text-main-darkgray">
                      {note}
                    </p>
                  ))}
                </div>

                {/* ボタン */}
                <div className="mt-auto">
                  {user ? (
                    <DashboardLink>
                      <FlatButton className="w-full" outline={!plan.recommended}>
                        プランを変更する
                      </FlatButton>
                    </DashboardLink>
                  ) : (
                    <FlatButton
                      className="w-full"
                      outline={!plan.recommended}
                      onClick={() => navigate('/login')}
                    >
                      {plan.buttonText}
                    </FlatButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* プラン比較 */}
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 pb-2 border-b-4 border-main-green">
            ひとつの契約で3サービス同時利用
          </h2>
          <p className="mb-4 sm:mb-6 text-sm sm:text-base">
            SDGs 共創 Lodgeの月額プランに加入すると、SDGs Lodges
            の各種サービスである「SDGs PR Lodge」と「SDGs クチコミ
            Lodge」の同一期間プランも自動的に付帯します。
          </p>

          {/* プラン加入例 */}
          <div className="bg-main-lightgreen p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
            <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">
              プラン加入例
            </h3>
            <ul className="space-y-1 text-xs sm:text-sm">
              <li>
                ・1ヶ月プラン（8万円/月）に加入すると、両サービスの
                1ヶ月プランが利用可能。
              </li>
              <li>
                ・半期プラン（7.5万円/月）に加入すると、両サービスの 半期プラン
                が利用可能。
              </li>
              <li>
                ・年間プラン（7万円/月）に加入すると、両サービスの 年間プラン
                が利用可能。
              </li>
            </ul>
          </div>

          <p className="mb-4 sm:mb-6 text-sm sm:text-base">
            つまり、ひとつの契約で、3つのサービスが同時に使えるお得なシステムです。
            <br />
            あなたのSDGs活動を、「発信」「共有」「拡散」で一気に加速させましょう。
          </p>

          <div className="mb-4 sm:mb-6 h-8 sm:h-12 bg-main-gray"></div>

          {/* 比較表 */}
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <table className="min-w-max w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-main-gray">
                  <th className="text-left pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    サービス
                  </th>
                  <th className="text-left pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    項目
                  </th>
                  <th className="text-center pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    フリープラン
                  </th>
                  <th className="text-center pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    1ヶ月プラン
                  </th>
                  <th className="text-center pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    半年プラン
                  </th>
                  <th className="text-center pt-2 px-2 sm:px-3 lg:px-4 font-bold text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    年間プラン
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* SDGs PR Lodge */}
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 whitespace-nowrap">
                    <span
                      className="font-bold text-main-green underline cursor-pointer text-xs sm:text-sm lg:text-base"
                      onClick={() =>
                        window.open(process.env.REACT_APP_PR_BASE_URL, '_blank')
                      }
                    >
                      SDGs PR Lodge
                    </span>
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    プレスリリース配信
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    3万円／回
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    30回／月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    30回／月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    30回／月
                  </td>
                </tr>

                {/* SDGs クチコミ Lodge */}
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 whitespace-nowrap">
                    <span
                      className="font-bold text-main-green underline cursor-pointer text-xs sm:text-sm lg:text-base"
                      onClick={() =>
                        window.open(
                          process.env.REACT_APP_KUCHIKOMI_BASE_URL,
                          '_blank'
                        )
                      }
                    >
                      SDGs クチコミ Lodge
                    </span>
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    クチコミ閲覧
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ×
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    100回/月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                </tr>
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4">&nbsp;</td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    クチコミ非表示権限
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ×
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    100回/月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                </tr>
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4">&nbsp;</td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    クチコミコメント返信
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ×
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    100回/月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    無制限
                  </td>
                </tr>
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4">&nbsp;</td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ユーザーへのポイント付与
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    10pt
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    100pt/月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    500pt/月
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    500pt/月
                  </td>
                </tr>
                <tr className="border-b border-main-gray">
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4">&nbsp;</td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    チケット掲載
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ×
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ○
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ○
                  </td>
                  <td className="pt-4 sm:pt-6 px-2 sm:px-3 lg:px-4 text-center text-xs sm:text-sm lg:text-base whitespace-nowrap">
                    ○
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}

export default PlanSection
